import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { TranslateService } from 'src/app/modules/core/services';
import { WarningsService } from 'src/app/modules/core/services/warning.service';
import { WarningMessageHelper } from 'src/app/modules/shared/helpers/warning-message-helper';
import { VehicleWarning, WarningMessageType } from 'src/app/modules/shared/models';
import { WarningMessageTypePipe } from '../../../modules/shared/pipes/warning-message.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FeedbackTextComponent } from '../../../modules/shared/components/feedback-text/feedback-text.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { WarningIconComponent } from '../../../modules/shared/components/warning-icon/warning-icon.component';
import { NgClass, DatePipe } from '@angular/common';

// eslint-disable-next-line no-shadow
export enum VehicleWarningConfirmState {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    NoDisplay = 0,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    DisplayConfirmDialog = 1,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    DisplayUndoMessage = 2,
}

@Component({
    selector: 'app-restore-warning',
    templateUrl: './restore-warning.component.html',
    styleUrls: ['./restore-warning.component.scss'],
    imports: [
        WarningIconComponent, ReactiveFormsModule, FormsModule, NgClass, FeedbackTextComponent, 
        MatProgressBarModule, DatePipe, TranslateModule, WarningMessageTypePipe
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class RestoreWarningComponent implements OnChanges {
    private readonly _warningService = inject(WarningsService);
    private readonly _warningHelper = inject(WarningMessageHelper);
    private readonly _ts = inject(TranslateService);

    @Input() warning: VehicleWarning;
    @Input() inventoryNumber: string;

    public commentField: string;
    public warningConfirmState: VehicleWarningConfirmState = VehicleWarningConfirmState.DisplayConfirmDialog;
    public requestQueued: boolean;
    public progress = 100;

    protected readonly vehicleWarningConfirmState = VehicleWarningConfirmState;

    private _confirmRequestTimeoutTimer: number;
    private _intervalProgressTimer: any;


    constructor() { }

    public get reasonPlaceholder() {
        if (this.warning) {
            return this._ts.instant('Note') + ' ' + this._warningHelper.getShortHeader(this.warning.messageType) + ' ' + this._ts.instant('reason');
        } else {
            return '';
        }
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.inventoryNumber?.firstChange === false || simpleChanges.warning?.firstChange === false) {
            this.warningConfirmState = VehicleWarningConfirmState.DisplayConfirmDialog;
            this.requestQueued = false;
            this.progress = 100;
            this.commentField = '';
            clearTimeout(this._confirmRequestTimeoutTimer);
            clearInterval(this._intervalProgressTimer);
        }
    }

    public confirm() {
        const maxTimeInSeconds = 10;
        const steps = 100; // Number of steps to reach 100%
        const interval = maxTimeInSeconds / steps;

        this.requestQueued = true;
        this.warningConfirmState = VehicleWarningConfirmState.DisplayUndoMessage;

        this._intervalProgressTimer = setInterval(() => {
            this.progress -= (100 / steps);
            if (this.progress <= 0) {
                clearInterval(this._intervalProgressTimer);
            }
        }, interval * 1000);

        this._confirmRequestTimeoutTimer = window.setTimeout(async () => {
            if (this.requestQueued === false) {
                return;
            }
            this.warningConfirmState = VehicleWarningConfirmState.NoDisplay;
            await this._sendRequest();
            this.commentField = '';
            this.requestQueued = false;
            this.progress = 100;
        }, maxTimeInSeconds * 1000);
    }

    public cancel() {
        clearTimeout(this._confirmRequestTimeoutTimer);
        clearInterval(this._intervalProgressTimer);
        this.progress = 100;
        this.warningConfirmState = VehicleWarningConfirmState.DisplayConfirmDialog;
        this.requestQueued = false;
    }

    private async _sendRequest() {
        const messageId = this.warning?.metadata?.messageId;
        switch (this.warning.messageType) {
            case WarningMessageType.ImmobilizerBypass:
                await this._warningService.confirmImmobilizeBypass(this.inventoryNumber, this.commentField);
                break;
            case WarningMessageType.TripNotStartedWarning:
                await this._warningService.confirmTripNotStarted(messageId, this.commentField);
                break;
            case WarningMessageType.OffRouteWarning:
                await this._warningService.confirmOffRouteWarning(messageId, this.commentField);
                break;
            case WarningMessageType.SignOnMissingWarning: {
                await this._warningService.confirmSignOnMissingWarning(messageId, this.commentField);
                break;
            }
        }
    }
}
